import { AnalyticsService } from '@/services/analytics.js'
import { Combobox } from '@/components/ui/Combobox.jsx'
import { useEffect, useState } from 'react'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { IntegrationProviders, UserRoles } from '@/consts/index.js'
import { AdAccountsDialog } from '@/components/ad-account-select/AdAccountsDialog.jsx'
import { handleError } from '@/services/errorHandler.js'
import { toast } from 'sonner'
import * as API from '@/services/api.js'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { useNavigate, useParams } from 'react-router-dom'
import { useRoleGuard } from '@/hooks/useRoleGuard.jsx'


export const AdAccountSelect = ({ dialogOnly, isDialogOpenByParent, setIsDialogOpenByParent }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(isDialogOpenByParent)
    const { adAccountsContext } = useDashboardContext()
    const { chosenAdAccount, setChosenAdAccount } = adAccountsContext
    const { integrations } = useDashboardContext().integrationsContext
    const { updateSpaceState } = useAuthContext()
    const { space } = useCurrentSpace()
    const { accountId } = useParams()
    const navigate = useNavigate()
    const isAccountAdmin = useRoleGuard(UserRoles.ADMIN, 'account')

    const metaIntegration = integrations.find(integration => integration.provider === IntegrationProviders.META)

    const navigateToSpaceDataSourceSettingPage = () => {
        navigate(`/account/${accountId}/space/${space._id}/dashboard/settings/space/data-sources`)
    }

    const setDefaultAttributionWindow = async () => {
        try {
            // If space doesn't have attribution windows set, set defaults
            if (!space.attributionWindowClick || !space.attributionWindowView) {
                const defaultAttributionWindows = {
                    attributionWindowClick: { "key": "7d_click", "label": "7 Day Click" },
                    attributionWindowView: { "key": "1d_view", "label": "1 Day View" }
                }

                await API.updateSpace(space._id, defaultAttributionWindows)
                updateSpaceState(space._id, { ...space, ...defaultAttributionWindows })
            }
        }
        catch (e) {
            console.log(e)
            handleError(e)
        }
    }

    // This is currently no in use since it should be configure from the Space settings. we will check about it soon
    const onDataSourceSelected = async (adAccountsIds) => {
        try {
            setIsDialogOpen(false)
            toggleLoader(true)

            // Get the full ad account objects from metaIntegration
            const selectedAdAccounts = metaIntegration.adAccounts
                .filter(adAccount => adAccountsIds.includes(adAccount.id))
                .map(adAccount => {
                    return {
                        ...adAccount,
                        provider: 'meta'
                    }
                })

            // Update space with selected ad accounts
            await API.updateSpace(space._id, { adAccounts: selectedAdAccounts })

            // Update space in global state
            const updatedSpace = { ...space, adAccounts: selectedAdAccounts }
            updateSpaceState(space._id, updatedSpace)

            toast.success('Ad Accounts updated successfully')
        } catch (e) {
            console.log(e)
            toast.error(e.message)
            handleError(e)
        } finally {
            toggleLoader(false)
        }
    }

    useEffect(() => {
        // Check if space exists and has attribution windows
        if (space) {
            if (!space.attributionWindowClick || !space.attributionWindowView) {
                setDefaultAttributionWindow()
            }

            if (!space.adAccounts || !space.adAccounts.length) {
                setChosenAdAccount(null)
            }

            // Check if space has ad accounts
            if (space.adAccounts && space.adAccounts.length > 0) {
                // If no ad account is chosen, select the first one
                if (!chosenAdAccount) {
                    setChosenAdAccount(space.adAccounts[0]?.id)
                    return
                }

                // If chosen ad account is not in selected space, update it. Could happen when switch from one space to another.
                if (!space.adAccounts.find(adAccount => adAccount.id === chosenAdAccount)) {
                    setChosenAdAccount(space.adAccounts[0]?.id)
                }

                return
            }

            // TODO - if the user has permission for this
            if (metaIntegration?.adAccounts?.length > 0) {
                // If space has no ad accounts but meta integration has them, show dialog
                setIsDialogOpen(true)
            }
        }
    }, [space, metaIntegration])

    useEffect(() => {
        if (isDialogOpenByParent) {
            setIsDialogOpen(isDialogOpenByParent)
        }
    }, [isDialogOpenByParent])

    useEffect(() => {
        if (setIsDialogOpenByParent) {
            setIsDialogOpenByParent(isDialogOpen)
        }
    }, [isDialogOpen])


    return (
        <>
            <AdAccountsDialog
                isDialogOpen={isDialogOpen}
                setIsDialogOpen={setIsDialogOpen}
                title="Choose Ad Accounts"
                description="Only the chosen ad accounts will be available on this account"
                onConfirm={onDataSourceSelected}
                chosenAdAccountsIds={space?.adAccounts?.map(adAccount => adAccount.id)}
            />

            {
                !dialogOnly && (
                    <Combobox
                        // onConfigureAdAccounts={(metaIntegration?.adAccounts && isAdminUser) ? () => setIsDialogOpen(true) : null}
                        onConfigureAdAccounts={isAccountAdmin ? navigateToSpaceDataSourceSettingPage : null}
                        placeholder="Choose Ad Account..."
                        className="px-2 text-xsm h-8 mb-2"
                        value={chosenAdAccount}
                        setValue={(chosen) => {
                            AnalyticsService.track('ad-account-changed', { toAdAccount: chosen, fromAdAccount: chosenAdAccount })
                            setChosenAdAccount(chosen.value)
                        }}
                        onSearch={() => { }}
                        options={space?.adAccounts ? space.adAccounts.map(adAccount => ({ label: adAccount.name, value: adAccount.id })) : []}
                    />
                )
            }


        </>
    )
}